import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import CrudTable from "@/components/common/CrudTable";
import { SectionCard } from "@/components/common/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, Settings as SettingsIcon } from "lucide-react";

function SettingsCard() {
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/settings").then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, []);
  const save = async () => {
    try {
      const payload = Object.entries(items).map(([key, value]) => ({ key, value: String(value) }));
      await api.put("/settings", payload);
      toast.success("Settings saved");
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (loading) return <div className="text-sm text-slate-500">Loading...</div>;

  // Grade thresholds & Score scale are now managed exclusively in Formula Engine.
  // Hide them here to avoid duplicate configuration surfaces (values remain in DB for migration safety).
  const HIDDEN_KEYS = new Set(["grade_a_min", "grade_b_min", "grade_c_min", "score_scale_min", "score_scale_max"]);
  const visibleEntries = Object.entries(items).filter(([k]) => !HIDDEN_KEYS.has(k));

  return (
    <SectionCard title="System Settings" subtitle="Application-wide configuration">
      <div className="mb-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="settings-formula-notice">
        <SettingsIcon size={13} className="mt-0.5 shrink-0 text-sky-700" />
        <div>
          <b>Grade &amp; Score configuration moved.</b> These are now managed exclusively in <b>Administration → System Parameter → Formula Engine</b>. Any change there is versioned and audit-tracked.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        {visibleEntries.map(([key, value]) => (
          <div key={key}>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{key}</Label>
            <Input value={value} onChange={(e) => setItems({ ...items, [key]: e.target.value })} className="mt-1 h-10" data-testid={`setting-${key}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} className="mt-4 bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="settings-save-button">Save Settings</Button>
    </SectionCard>
  );
}

export default function AdminPage() {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">System Parameter › Administration</div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Administration</h1>
        <p className="text-sm text-slate-600 mt-1">User accounts and application-wide configuration. Master data (Suppliers, Plants, Categories, Questions) is now managed under <b>Supplier Management</b>.</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-slate-200 rounded-md p-1">
          <TabsTrigger value="users" data-testid="tab-users" className="gap-2">
            <Users size={13} /> Users
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="gap-2">
            <SettingsIcon size={13} /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <CrudTable
            title="Users" subtitle="System accounts and role assignments" endpoint="/users"
            fields={[
              { key: "email", label: "Email", type: "email", required: true },
              { key: "name", label: "Full name", required: true },
              { key: "password", label: "Password", required: true },
              { key: "role", label: "Role", type: "select", required: true, options: [
                { value: "system_admin", label: "System Administrator" },
                { value: "sd_admin", label: "SD Admin" },
                { value: "auditor", label: "Auditor" },
                { value: "manager", label: "Manager" },
                { value: "supplier", label: "Supplier" },
              ]},
            ]}
            columns={[
              { key: "name", label: "Name", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
              { key: "email", label: "Email", className: "font-mono text-xs" },
              { key: "role", label: "Role" },
              { key: "active", label: "Active", render: (r) => r.active ? "Yes" : "No" },
            ]}
            testIdPrefix="user"
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <SettingsCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
